// pages/auth/Register.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authActions } from '../../../core/action/authActions';
import { companyActions } from '../../../core/action/companyActions';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { 
    Mail,  User, Phone, MapPin, 
    Eye, EyeOff, Upload, Calendar,
} from 'feather-icons-react/build/IconComponents';
import ImageWithBasePath from '../../../core/img/imagewithbasebath';
import Select from 'react-select';

const MySwal = withReactContent(Swal);

const Register = () => {
    const navigate = useNavigate();
    
    // State
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        password: '',
        confirmPassword: '',
        role_id: '4', // Default to Employee
        department: '',
        designation: '',
        joining_date: '',
        gender: '',
        address: '',
        city: '',
        state: '',
        country: '',
        pincode: ''
    });
    
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [profileImage, setProfileImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [companySettings, setCompanySettings] = useState({});
    
    // Role options
    const roleOptions = [
        { value: '2', label: 'Admin' },
        { value: '3', label: 'Manager' },
        { value: '4', label: 'Employee' }
    ];
    
    // Gender options
    const genderOptions = [
        { value: 'male', label: 'Male' },
        { value: 'female', label: 'Female' },
        { value: 'other', label: 'Other' }
    ];
    
    // Load company settings
    useEffect(() => {
        loadCompanySettings();
        
        // Check if user is already logged in
        if (authActions.isAuthenticated()) {
            navigate('/dashboard');
        }
    }, [navigate]);
    
    const loadCompanySettings = async () => {
        try {
            const result = await companyActions.getSettings();
            if (result.success) {
                setCompanySettings(result.data);
            }
        } catch (error) {
            console.error('Error loading company settings:', error);
        }
    };
    
    // Handle input change
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };
    
    // Handle select change
    const handleSelectChange = (name, option) => {
        setFormData(prev => ({ ...prev, [name]: option?.value || '' }));
    };
    
    // Handle file change
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setProfileImage(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };
    
    // Validate form
    const validateForm = () => {
        if (!formData.name) {
            MySwal.fire('Error', 'Name is required', 'error');
            return false;
        }
        if (!formData.email) {
            MySwal.fire('Error', 'Email is required', 'error');
            return false;
        }
        if (!/\S+@\S+\.\S+/.test(formData.email)) {
            MySwal.fire('Error', 'Email is invalid', 'error');
            return false;
        }
        if (!formData.password) {
            MySwal.fire('Error', 'Password is required', 'error');
            return false;
        }
        if (formData.password.length < 6) {
            MySwal.fire('Error', 'Password must be at least 6 characters', 'error');
            return false;
        }
        if (formData.password !== formData.confirmPassword) {
            MySwal.fire('Error', 'Passwords do not match', 'error');
            return false;
        }
        return true;
    };
    
    // Handle form submit
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) return;
        
        setLoading(true);
        
        // Create FormData for file upload
        const submitData = new FormData();
        
        // Append all form fields
        Object.keys(formData).forEach(key => {
            if (formData[key]) {
                submitData.append(key, formData[key]);
            }
        });
        
        // Append profile image
        if (profileImage) {
            submitData.append('profile_image', profileImage);
        }
        
        // Log FormData contents for debugging
        console.log('Submitting FormData:');
        for (let pair of submitData.entries()) {
            console.log(pair[0] + ': ' + pair[1]);
        }
        
        const result = await authActions.register(submitData);
        
        if (result && result.success) {
            MySwal.fire({
                icon: 'success',
                title: 'Registration Successful',
                text: result.message || 'Your account has been created. Please login to continue.',
                timer: 3000,
                showConfirmButton: true
            }).then(() => {
                navigate('/signin');
            });
        } else {
            MySwal.fire({
                icon: 'error',
                title: 'Registration Failed',
                text: result?.message || 'Something went wrong. Please try again.'
            });
        }
        
        setLoading(false);
    };
    
    return (
        <div className="account-content">
            <div className="login-wrapper">
                <div className="login-content">
                    <div className="login-userset" style={{ maxWidth: '800px' }}>
                        {/* Logo */}
                        <div className="login-logo">
                            {companySettings.company_logo ? (
                                <img 
                                    src={`/${companySettings.company_logo}`} 
                                    alt={companySettings.company_name}
                                    style={{ maxHeight: '50px' }}
                                />
                            ) : (
                                <h2>{companySettings.company_name || 'Company'}</h2>
                            )}
                        </div>
                        
                        <div className="login-userheading">
                            <h3>Create Account</h3>
                            <h4>Fill in your details to register</h4>
                        </div>
                        
                        <form onSubmit={handleSubmit}>
                            <div className="row">
                                {/* Profile Image */}
                                <div className="col-12 mb-3 text-center">
                                    <div className="profile-upload">
                                        <div className="profile-img">
                                            {imagePreview ? (
                                                <img 
                                                    src={imagePreview} 
                                                    alt="Preview" 
                                                    className="rounded-circle"
                                                    style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                                                />
                                            ) : (
                                                <div className="avatar avatar-xl bg-light rounded-circle d-flex align-items-center justify-content-center">
                                                    <User size={40} className="text-muted" />
                                                </div>
                                            )}
                                        </div>
                                        <div className="mt-2">
                                            <label htmlFor="profile_image" className="btn btn-outline-primary btn-sm">
                                                <Upload size={14} className="me-1" />
                                                Upload Photo
                                            </label>
                                            <input
                                                type="file"
                                                id="profile_image"
                                                className="d-none"
                                                accept="image/*"
                                                onChange={handleFileChange}
                                            />
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Name */}
                                <div className="col-md-6">
                                    <div className="form-login">
                                        <label>Full Name *</label>
                                        <div className="pass-group">
                                            <input
                                                type="text"
                                                name="name"
                                                className="form-control"
                                                placeholder="Enter your full name"
                                                value={formData.name}
                                                onChange={handleInputChange}
                                                disabled={loading}
                                                required
                                            />
                                            <span className="input-icon">
                                                <User size={16} />
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Email */}
                                <div className="col-md-6">
                                    <div className="form-login">
                                        <label>Email *</label>
                                        <div className="pass-group">
                                            <input
                                                type="email"
                                                name="email"
                                                className="form-control"
                                                placeholder="Enter your email"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                disabled={loading}
                                                required
                                            />
                                            <span className="input-icon">
                                                <Mail size={16} />
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Phone */}
                                <div className="col-md-6">
                                    <div className="form-login">
                                        <label>Phone</label>
                                        <div className="pass-group">
                                            <input
                                                type="tel"
                                                name="phone"
                                                className="form-control"
                                                placeholder="Enter your phone number"
                                                value={formData.phone}
                                                onChange={handleInputChange}
                                                disabled={loading}
                                            />
                                            <span className="input-icon">
                                                <Phone size={16} />
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Role - Hidden or visible based on your requirement */}
                                <div className="col-md-6">
                                    <div className="form-login">
                                        <label>Role *</label>
                                        <Select
                                            options={roleOptions}
                                            value={roleOptions.find(opt => opt.value === formData.role_id)}
                                            onChange={(opt) => handleSelectChange('role_id', opt)}
                                            placeholder="Select role"
                                            isDisabled={loading}
                                        />
                                    </div>
                                </div>
                                
                                {/* Department */}
                                <div className="col-md-6">
                                    <div className="form-login">
                                        <label>Department</label>
                                        <input
                                            type="text"
                                            name="department"
                                            className="form-control"
                                            placeholder="Enter department"
                                            value={formData.department}
                                            onChange={handleInputChange}
                                            disabled={loading}
                                        />
                                    </div>
                                </div>
                                
                                {/* Designation */}
                                <div className="col-md-6">
                                    <div className="form-login">
                                        <label>Designation</label>
                                        <input
                                            type="text"
                                            name="designation"
                                            className="form-control"
                                            placeholder="Enter designation"
                                            value={formData.designation}
                                            onChange={handleInputChange}
                                            disabled={loading}
                                        />
                                    </div>
                                </div>
                                
                                {/* Joining Date */}
                                <div className="col-md-6">
                                    <div className="form-login">
                                        <label>Joining Date</label>
                                        <div className="pass-group">
                                            <input
                                                type="date"
                                                name="joining_date"
                                                className="form-control"
                                                value={formData.joining_date}
                                                onChange={handleInputChange}
                                                disabled={loading}
                                            />
                                            <span className="input-icon">
                                                <Calendar size={16} />
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Gender */}
                                <div className="col-md-6">
                                    <div className="form-login">
                                        <label>Gender</label>
                                        <Select
                                            options={genderOptions}
                                            value={genderOptions.find(opt => opt.value === formData.gender)}
                                            onChange={(opt) => handleSelectChange('gender', opt)}
                                            placeholder="Select gender"
                                            isClearable
                                            isDisabled={loading}
                                        />
                                    </div>
                                </div>
                                
                                {/* Password */}
                                <div className="col-md-6">
                                    <div className="form-login">
                                        <label>Password *</label>
                                        <div className="pass-group">
                                            <input
                                                type={showPassword ? 'text' : 'password'}
                                                name="password"
                                                className="form-control"
                                                placeholder="Enter password"
                                                value={formData.password}
                                                onChange={handleInputChange}
                                                disabled={loading}
                                                required
                                            />
                                            <span 
                                                className="toggle-password"
                                                onClick={() => setShowPassword(!showPassword)}
                                            >
                                                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Confirm Password */}
                                <div className="col-md-6">
                                    <div className="form-login">
                                        <label>Confirm Password *</label>
                                        <div className="pass-group">
                                            <input
                                                type={showConfirmPassword ? 'text' : 'password'}
                                                name="confirmPassword"
                                                className="form-control"
                                                placeholder="Confirm password"
                                                value={formData.confirmPassword}
                                                onChange={handleInputChange}
                                                disabled={loading}
                                                required
                                            />
                                            <span 
                                                className="toggle-password"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            >
                                                {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Address */}
                                <div className="col-12">
                                    <div className="form-login">
                                        <label>Address</label>
                                        <div className="pass-group">
                                            <textarea
                                                name="address"
                                                className="form-control"
                                                rows="2"
                                                placeholder="Enter your address"
                                                value={formData.address}
                                                onChange={handleInputChange}
                                                disabled={loading}
                                            ></textarea>
                                            <span className="input-icon">
                                                <MapPin size={16} />
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* City - Single instance */}
                                <div className="col-md-4">
                                    <div className="form-login">
                                        <label>City</label>
                                        <input
                                            type="text"
                                            name="city"
                                            className="form-control"
                                            placeholder="Enter city"
                                            value={formData.city}
                                            onChange={handleInputChange}
                                            disabled={loading}
                                        />
                                    </div>
                                </div>
                                
                                {/* State - Single instance */}
                                <div className="col-md-4">
                                    <div className="form-login">
                                        <label>State</label>
                                        <input
                                            type="text"
                                            name="state"
                                            className="form-control"
                                            placeholder="Enter state"
                                            value={formData.state}
                                            onChange={handleInputChange}
                                            disabled={loading}
                                        />
                                    </div>
                                </div>
                                
                                {/* Pincode - Single instance */}
                                <div className="col-md-4">
                                    <div className="form-login">
                                        <label>Pincode</label>
                                        <input
                                            type="text"
                                            name="pincode"
                                            className="form-control"
                                            placeholder="Enter pincode"
                                            value={formData.pincode}
                                            onChange={handleInputChange}
                                            disabled={loading}
                                        />
                                    </div>
                                </div>
                                
                                {/* Country - Hidden or optional */}
                                <input type="hidden" name="country" value={formData.country} />
                                
                                {/* Submit Button */}
                                <div className="col-12">
                                    <div className="form-login">
                                        <button 
                                            type="submit" 
                                            className="btn btn-primary w-100"
                                            disabled={loading}
                                        >
                                            {loading ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                                    Creating Account...
                                                </>
                                            ) : (
                                                'Register'
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </form>
                        
                        {/* Login Link */}
                        <div className="signinform text-center mt-3">
                            <h4>
                                Already have an account?{' '}
                                <Link to="/signin" className="hover-a">
                                    Sign In
                                </Link>
                            </h4>
                        </div>
                    </div>
                </div>
                
                {/* Right Side Image */}
                <div className="login-img">
                    <ImageWithBasePath 
                        src="assets/img/login.jpg" 
                        alt="Register" 
                        className="img-fluid"
                    />
                </div>
            </div>
        </div>
    );
};

export default Register;